<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { Invoice } from '../../../../Types/invoice'

defineProps<{
  invoice: Invoice
}>()

const { n, t } = useI18n()
</script>

<template>
  <div class="table-responsive">
    <table class="table table-striped mb-0">
      <thead>
        <tr>
          <th class="font-weight-bold col-2">
            {{ t('invoice.label.contract') }}
          </th>
          <th class="font-weight-bold col-4">
            {{ t('invoice.label.purchaseOrderNumbers') }}
          </th>
          <th class="font-weight-bold col-2">
            {{ t('invoice.label.itemType') }}
          </th>
          <th class="font-weight-bold col-2">
            {{ t('invoice.label.vat') }}
          </th>
          <th class="font-weight-bold col-2">
            {{ t('invoice.label.unitAmount') }}
          </th>
          <th class="font-weight-bold col-2">
            {{ t('invoice.label.quantity') }}
          </th>
          <th class="font-weight-bold col-2">
            {{ t('invoice.label.amount') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in invoice.items" :key="item.id">
          <td>{{ item.purchaseOrderItem.purchaseOrder.contract?.label }}</td>
          <td>{{ item.purchaseOrderItem.purchaseOrder.number ? item.purchaseOrderItem.purchaseOrder.number : '-' }}</td>
          <td>{{ item.itemType.label }}</td>
          <td>{{ n(Number(item.taxType.amount), 'decimal') + ' %' }}</td>
          <td>{{ item.unitAmount ? n(Number(item.unitAmount), 'currency') : '-' }}</td>
          <td>{{ item.quantity }}</td>
          <td>{{ item.amount ? n(Number(item.amount), 'currency') : '-' }}</td>
        </tr>
        <tr v-if="!invoice.deposit">
          <td class="text-right" colspan="4">{{ t('invoice.label.totalAllocated') }}</td>
          <td class="text-right" colspan="3">
            {{ invoice.itemsAmount ? n(Number(invoice.itemsAmount), 'currency') : '-' }}
          </td>
        </tr>
        <tr>
          <td class="text-right" colspan="4">{{ t('invoice.label.amountAvailable') }}</td>
          <td class="text-right" colspan="3">
            {{
              invoice.itemsAmount && invoice.amount ? n(Number(invoice.amount - invoice.itemsAmount), 'currency') : '-'
            }}
          </td>
        </tr>
        <tr>
          <td class="text-right" colspan="4">{{ t('invoice.label.vatAmount') }}</td>
          <td class="text-right" colspan="3">
            {{ invoice.vatAmount ? n(Number(invoice.vatAmount), 'currency') : '-' }}
          </td>
        </tr>
        <tr>
          <td class="text-right" colspan="4">{{ t('invoice.label.amountAllocation') }}</td>
          <td class="text-right" colspan="3">
            {{ invoice.itemsAmountWithVat ? n(Number(invoice.itemsAmountWithVat), 'currency') : '-' }}
          </td>
        </tr>
        <tr>
          <td class="text-right" colspan="4">{{ t('invoice.label.totalInvoice') }}</td>
          <td class="text-right" colspan="3">{{ invoice.total ? n(Number(invoice.total), 'currency') : '-' }}</td>
        </tr>
        <tr v-if="invoice.prepayment && invoice.prepayment > 0">
          <td class="text-right" colspan="4">{{ t('invoice.label.prepayment') }}</td>
          <td class="text-right" colspan="3">{{ n(Number(invoice.prepayment), 'currency') }}</td>
        </tr>
        <tr v-if="invoice.prepaymentClient && invoice.prepaymentClient > 0">
          <td class="text-right" colspan="4">{{ t('invoice.label.prepaymentClient') }}</td>
          <td class="text-right" colspan="3">{{ n(Number(invoice.prepaymentClient), 'currency') }}</td>
        </tr>
        <tr v-if="invoice.deposits && invoice.deposits.length > 0">
          <td class="text-right" colspan="4">{{ t('invoice.label.deposits') }}</td>
          <td class="text-right" colspan="3">
            {{ n(Number(invoice.deposits.reduce((total, allocation) => total + allocation.total, 0)), 'currency') }}
          </td>
        </tr>
        <tr v-if="invoice.refundClientDepositAmount && invoice.refundClientDepositAmount > 0">
          <td class="text-right" colspan="4">{{ t('invoice.label.refundClientDepositAmount') }}</td>
          <td class="text-right" colspan="3">{{ n(Number(invoice.refundClientDepositAmount), 'currency') }}</td>
        </tr>
        <tr>
          <td class="text-right" colspan="4">{{ t('invoice.label.totalToPay') }}</td>
          <td class="text-right" colspan="3">{{ n(Number(invoice.totalToPay), 'currency') }}</td>
        </tr>
        <tr>
          <td class="text-right" colspan="4">{{ t('invoice.label.balance') }}</td>
          <td class="text-right" colspan="3">{{ n(Number(invoice.balance), 'currency') }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
